import { useCallback, useContext } from 'react';
import { RendererContext } from '../../contexts/RendererContext';
import { GroupType } from '../../types/graphqlTypes';
import { usePersonaManager } from '../usePersonaManager';

/**
 * Returns a predicate deciding whether the left panel should HIDE an annotation
 * of the given `type` for the active persona.
 *
 * The Layout persona is a "sketch" workspace: it uses an ALLOWLIST — only sketch
 * objects (WALL_SKETCH, OPENING_SKETCH, SKETCH_OBJECT) are shown, everything
 * else is hidden (rooms, outlines, openings, points, areas, …). The floorplan
 * persona shows everything INCLUDING the sketch types (its wall + design modes
 * create/edit them). Every other persona hides all sketch types. Floors are
 * groups (not annotations) and stay visible everywhere.
 *
 * Mirrors the viewport gating in `applyAnnotationBlacklist` so panel + scene
 * agree.
 */
const SKETCH_TYPES = new Set<string>(['WALL_SKETCH', 'OPENING_SKETCH', 'SKETCH_OBJECT']);

/**
 * Group-row filtering per persona: room folders ("tags") belong to the
 * floorplan persona's Design tab and hide everywhere else; the Layout persona
 * additionally keeps only FLOOR groups (its sketch allowlist).
 */
export function useShouldHideGroupType(): (type: GroupType) => boolean {
  const viewer = useContext(RendererContext).viewer;
  const { activePersona, currentMode } = usePersonaManager(viewer);
  const personaName = activePersona?.name;
  const modeId = currentMode?.id;
  return useCallback(
    (type: GroupType) => {
      if (type === GroupType.Room) return !(personaName === 'floorplan' && modeId === 'design');
      if (personaName === 'layout') return type !== GroupType.Floor;
      return false;
    },
    [personaName, modeId],
  );
}

export function useShouldHideAnnotationType(): (type: string) => boolean {
  const viewer = useContext(RendererContext).viewer;
  const { activePersona, currentMode } = usePersonaManager(viewer);
  const personaName = activePersona?.name;
  const modeId = currentMode?.id;
  return useCallback(
    (type: string) => {
      if (personaName === 'layout') return !SKETCH_TYPES.has(type);
      if (personaName === 'floorplan') {
        // The Floor tab is about floors only — every annotation inside them
        // is hidden (the floor group rows themselves are groups, not
        // annotations, and stay).
        if (modeId === 'floor_select') return true;
        // Room measures (the drawn room polygons) show in the Room tab (mode
        // id 'floorplan', named "room") and the Wall tab. Their room FOLDERS
        // (groups) stay visible in every mode.
        if (type === 'ROOM') return modeId !== 'floorplan' && modeId !== 'wall';
        // The wall sketch row shows in the Wall tab (which edits it) and the
        // Design tab; door/window and object rows are Design-only. NOTE:
        // panel-only for walls/openings — the Wall tab still renders and
        // edits them in the VIEWPORT (see applyAnnotationBlacklist where only
        // SKETCH_OBJECT is scene-hidden outside Design).
        if (type === 'WALL_SKETCH') return modeId !== 'design' && modeId !== 'wall';
        if (SKETCH_TYPES.has(type)) return modeId !== 'design';
        return false;
      }
      return SKETCH_TYPES.has(type);
    },
    [personaName, modeId],
  );
}
